<template>
  <div class="upload-page" @dragover="handleDragOver" @dragleave="handleDragLeave" @drop="handleDrop">


    <!-- vue-upload-component：仅作文件选择触发，不显示列表，上传由自定义逻辑处理 -->
    <file-upload
      ref="uploadRef"
      class="hidden-upload"
      name="file"
      :multiple="allowMultiple"
      :maximum="maxFiles"
      :size="maxFileSizeBytes"
      post-action=""
      :drop="false"
      @input-filter="handleInputFilter"
    />

    <!-- 全局拖拽指示器 -->
    <div v-if="isDragOver" class="global-drag-overlay">
      <div class="global-drag-content">
        <div class="global-drag-icon">📁</div>
        <div class="global-drag-text">释放文件到任意位置即可上传</div>
      </div>
    </div>


    <div class="upload-container">
      <div class="upload-card">
        <!-- 标题和添加按钮 -->
        <div class="upload-header">
          <h1>{{ pageTitle }}</h1>
          <button @click="triggerUpload" class="add-file-btn">
            <img src="../assets/add.png" alt="添加文件" class="add-icon" />
          </button>
        </div>

        <!-- 没有文件时显示上传区域 -->
        <div v-if="files.length === 0" class="upload-area" :class="{ 'drag-over': isDragOver }" @drop="handleDrop"
          @dragover="handleDragOver" @dragleave="handleDragLeave" @click="triggerUpload">
          <div class="upload-icon">
            <div class="folder-icon">📁</div>
          </div>
          <p class="upload-text">点击或拖拽文件到此处选择文件上传</p>
        </div>

        <!-- 有文件时显示文件列表 -->
        <div v-else class="upload-file-list">
          <div class="file-list-content">
            <div v-for="(file, index) in files" :key="file.id" class="file-item">
              <div class="file-info">
                <span class="file-icon">📄</span>
                <div class="file-details">
                  <div class="file-name-wrapper">
                    <div class="file-name">{{ file.name }}</div>
                    <span class="file-size">
                      <!-- 校验中：无进度，动态省略号让等待更有感知 -->
                      <span v-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING && file.isPreparing" class="status-text preparing-text">
                        文件正在校验中，请稍候<span class="preparing-dots"><span class="dot">.</span><span class="dot">.</span><span class="dot">.</span></span>
                      </span>
                      <span v-else-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING && file.uploadedSize !== undefined">
                        {{ fileUtil.formatFileSize(file.uploadedSize || 0) }}/{{ fileUtil.formatFileSize(file.size) }} ·
                        <span v-if="file.uploadSpeed && file.uploadSpeed > 0">{{
                          fileUtil.formatFileSize(file.uploadSpeed)
                          }}/s</span>
                        <span v-if="file.uploadSpeed && file.uploadSpeed > 0 && file.progress < 100 && file.size > 0" class="remaining-time">
                          剩余 {{ fileUtil.formatRemainingTime(((file.size - (file.uploadedSize || 0)) / file.uploadSpeed)) }}
                        </span>
                      </span>
                      <span v-else>
                        ({{ fileUtil.formatFileSize(file.size) }})
                      </span>
                    </span>
                  </div>
                  <!-- 校验中只显示文案，上传中显示圆形进度条 -->
                  <div v-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING && !file.isPreparing" class="circular-progress-container">
                    <progress-circle :diameter="40" :completed-steps="file.progress || 0" :total-steps="100"
                      circle-color="#e0e0e0" start-color="#3498db" :circle-width="2" :animation-duration="0.1">
                      <div class="progress-text">{{ (file.progress || 0).toFixed(1) }}%</div>
                    </progress-circle>
                  </div>
                </div>
              </div>
              <div class="file-actions">
                <span v-if="file.uploadStatus === UPLOAD_STATUS.PROCESSING"
                  class="status-text status-waiting">文件检查中...</span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.WAITING" class="status-text status-waiting">等待上传</span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.PENDING" class="status-text status-pending">待上传</span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING && file.isPreparing"
                  class="status-text status-uploading">校验中</span>
                <span v-else-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING"
                  class="status-text status-uploading">上传中</span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.SUCCESS" class="status-text status-success">上传成功</span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.SUCCESS" class="status-text status-success">
                  <img src="../assets/success-filling.png" alt="成功" class="status-icon" />
                </span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.ERROR" class="status-text status-error">上传失败</span>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.CANCELLED" class="status-text status-error">已取消</span>
                <button
                  v-if="file.uploadStatus === UPLOAD_STATUS.WAITING || file.uploadStatus === UPLOAD_STATUS.ERROR || file.uploadStatus === UPLOAD_STATUS.CANCELLED"
                  @click.stop="uploadSingleFile(file, index)" class="btn-icon-only" title="上传">
                  <img src="../assets/upload.png" alt="上传" class="btn-icon" />
                </button>
                <button
                  v-if="file.uploadStatus === UPLOAD_STATUS.WAITING || file.uploadStatus === UPLOAD_STATUS.CANCELLED || file.uploadStatus === UPLOAD_STATUS.ERROR"
                  @click.stop="removeFile(index)" class="btn-icon-only" title="删除">
                  <img src="../assets/delete.png" alt="删除" class="btn-icon" />
                </button>
                <button v-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING && !file.isPreparing"
                  @click.stop="pauseSingleUpload(file, index)" class="btn-icon-only" title="暂停">
                  <img src="../assets/pause.png" alt="暂停" class="btn-icon" />
                </button>
                <button v-if="file.uploadStatus === UPLOAD_STATUS.PAUSED" @click.stop="resumeSingleUpload(file, index)"
                  class="btn-icon-only" title="继续">
                  <img src="../assets/continue.png" alt="继续" class="btn-icon" />
                </button>
                <button
                  v-if="file.uploadStatus === UPLOAD_STATUS.UPLOADING || file.uploadStatus === UPLOAD_STATUS.PAUSED || file.uploadStatus === UPLOAD_STATUS.PENDING"
                  @click.stop="cancelSingleUpload(file, index)" class="btn-icon-only" title="取消">
                  <img src="../assets/close.png" alt="取消" class="btn-icon" />
                </button>
                <span v-if="file.uploadStatus === UPLOAD_STATUS.SUCCESS && file.downloadStatus === 'preparing'"
                  class="status-text status-preparing">准备中...</span>
                <button v-if="file.uploadStatus === UPLOAD_STATUS.SUCCESS && file.downloadStatus !== 'preparing'"
                  @click.stop="downloadFile(file, index)" class="btn-icon-only download-btn-with-badge" title="下载"
                  :disabled="file.downloadStatus === 'preparing'">
                  <img src="../assets/download.png" alt="下载" class="btn-icon" />
                  <span v-if="file.downloadCount > 0" class="download-badge">{{ file.downloadCount > 99 ? '99+' :
                    file.downloadCount }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 上传状态和设置 -->
        <div class="upload-status">
          <div class="status-item">
            <span class="status-label">已使用 {{ fileUtil.formatFileSize(totalFileSize) }} / {{ maxTotalSize }}</span>
            <span class="status-value">{{ Math.ceil(usagePercentage * 100) / 100 }}%</span>
          </div>

          <div class="status-item">
            <span class="status-label">剩余空间</span>
            <span class="status-value" :class="{ 'warning': usagePercentage > 80, 'danger': usagePercentage > 95 }">
              {{ fileUtil.formatFileSize(remainingSize) }}
            </span>
          </div>

          <div class="status-item progress-item">
            <div class="progress-bar">
              <div class="progress-fill" :class="{ 'warning': usagePercentage > 80, 'danger': usagePercentage > 95 }"
                :style="{ width: usagePercentage + '%' }"></div>
            </div>
          </div>

          <!-- <div class="status-item">
            <span class="status-label">加密传输</span>
            <div class="toggle-switch" :class="{ active: encryptedTransfer }" @click="toggleEncryption">
              <div class="toggle-slider"></div>
            </div>
          </div> -->
          <div class="status-item">
            <span class="status-label">总文件数限制</span>
            <span class="status-value">{{ files.length }}/100</span>
          </div>

          <div class="status-item">
            <span class="status-label">最大单文件限制</span>
            <span class="status-value">{{ maxFileSize }}</span>
          </div>

          <!-- 同时上传数量：暂不展示，默认 1 条 -->
          <div class="status-item">
            <span class="status-label">同时上传数量</span>
            <div class="concurrent-radio-group">
              <label class="radio-option">
                <input type="radio" :value="1" v-model="concurrentUploads" />
                <span>1个</span>
              </label>
              <label class="radio-option">
                <input type="radio" :value="2" v-model="concurrentUploads" />
                <span>2个</span>
              </label>
              <label class="radio-option">
                <input type="radio" :value="3" v-model="concurrentUploads" />
                <span>3个</span>
              </label>
            </div>
          </div>

          <div class="status-item">
            <span class="status-label">空间/文件有效期至</span>
            <span class="status-value">{{ expirationDate }}</span>
          </div>

          <!-- 分享链接 -->
          <div class="status-item share-link-item">
            <span class="status-label">分享链接</span>
            <div class="share-content">
              <div class="share-text" :title="shareLink">
                {{ shareLink || '上传文件后生成分享链接' }}
              </div>
              <button @click="copyShareLink" class="copy-btn" :disabled="!shareLink || uploadedFilesCount === 0">
                {{ copySuccess ? '已复制' : '复制' }}
              </button>
            </div>
          </div>

          <div class="status-item share-actions-item">
            <span class="status-label"></span>
            <div class="share-actions">
              <button @click="copyToEmail" class="email-btn" :disabled="uploadedFilesCount === 0">
                复制到邮件正文
              </button>
              <span class="help-text">不会用？</span>
              <a href="#" class="help-link" @click.prevent="showTutorial">查看教程</a>
            </div>
          </div>
        </div>

        <!-- 开始上传按钮 -->
        <div class="upload-actions">
          <button @click="startUpload" class="start-upload-btn" :disabled="files.length === 0 || uploading">
            {{ uploading ? '上传中...' : '开始上传任务' }}
          </button>
        </div>

      </div>
      
      <!-- 上传提示 -->
      <div class="upload-tip">
        <div class="tip-content">
          <span class="tip-icon">⚠️</span>
          <p class="tip-text">上传过程中，请勿刷新或离开界面，避免文件上传异常！</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { showDialog } from '../Dialog/index.js'
import request from '../api/request.js'
import { createLogger } from '../util/logger.js'
import { ProgressCircle } from 'vue-progress-circle'
import { copyToEmail as copyToEmailUtil } from '../util/email.js'
import { copyToClipboard } from '../util/clipboard.js'
import * as fileUtil from '../util/file.js'
import { UPLOAD_STATUS } from '../util/file.js'
import uploadStorage from '../util/uploadStorage.js'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { reportBehaviorLog, reportException } from '../util/behaviorLog.js'

import FileUpload from 'vue-upload-component'

const props = defineProps({
  icon: { type: String, default: '📁' },
  showText: { type: Boolean, default: true },
  allowMultiple: { type: Boolean, default: true },
  maxFiles: { type: Number, default: 10 },
  maxFileSize: { type: String, default: '10GB' },
  buttonText: { type: String, default: '选择文件' }
})

const emit = defineEmits(['files-selected', 'file-removed'])

const chunkSize = 1024 * 1024 // 1MB
const uploadRef = ref(null)

// vue-upload-component 的 size 为字节数
const maxFileSizeBytes = computed(() => fileUtil.parseFileSize(maxFileSize.value) || 0)

// Driver.js 实例
let driverInstance = null

const fileInfo = ref([])

const logger = createLogger('Upload')

const router = useRouter()
const route = useRoute()

// 响应式数据
const files = ref([])
const isDragOver = ref(false)
const uploading = ref(false)
const encryptedTransfer = ref(true)
const expirationDate = ref('-')
const shareLink = ref('')
const copySuccess = ref(false)


// 从路由参数获取配置
const maxFileSize = ref('50 GB')
const maxTotalSize = ref('5.00 GB')
// 若总容量大于单文件限制，则单文件限制取总容量大小
watch(
  [maxTotalSize, maxFileSize],
  ([total, single]) => {
    const totalBytes = fileUtil.parseFileSize(total)
    const singleBytes = fileUtil.parseFileSize(single)
    if (totalBytes > 0 && singleBytes < totalBytes) {
      maxFileSize.value = maxTotalSize.value
    }
  },
  { immediate: true }
)
const allowMultiple = ref(true)
const encryptionEnabled = ref(true)
const pageTitle = ref('文件上传')
const pageDescription = ref('支持拖拽上传，最大单文件50GB')

// 上传配置
const accessToken = ref('')
const parentFileId = ref('')

// 并发控制：用户可选择1-3个文件同时上传
const concurrentUploads = ref(1) // 同时上传的文件数量（1-3），暂默认1条
const pendingUploadQueue = [] // 待上传队列

let fileIdCounter = 0
let dragOverTimer = null // 拖拽状态定时器

// Screen Wake Lock：上传时保持屏幕/设备活跃，避免休眠中断上传
let wakeLockSentinel = null
let visibilityCleanup = null
const hasActiveUpload = () => files.value.some(f => f.uploadStatus === UPLOAD_STATUS.UPLOADING)
const requestWakeLock = async () => {
  if (!navigator.wakeLock || wakeLockSentinel) return
  try {
    wakeLockSentinel = await navigator.wakeLock.request('screen')
    wakeLockSentinel.addEventListener('release', () => { wakeLockSentinel = null })
    logger.info('Wake Lock 已请求，保持设备活跃')
  } catch (e) {
    logger.warn('Wake Lock 请求失败:', e?.message || e)
  }
}
const releaseWakeLock = async () => {
  if (!wakeLockSentinel) return
  try {
    await wakeLockSentinel.release()
    wakeLockSentinel = null
    logger.info('Wake Lock 已释放')
  } catch (e) {
    wakeLockSentinel = null
  }
}
const updateWakeLock = async () => {
  if (hasActiveUpload()) await requestWakeLock()
  else await releaseWakeLock()
}

// 获取当前正在上传的文件数量
const getActiveUploadCount = () => {
  return files.value.filter(file => file.uploadStatus === UPLOAD_STATUS.UPLOADING).length
}

// 处理待上传队列：当有文件完成上传后，自动从队列中取出下一个开始上传
const processPendingQueue = async () => {
  while (pendingUploadQueue.length > 0 && getActiveUploadCount() < concurrentUploads.value) {
    const { fileItem, index, isResume } = pendingUploadQueue.shift()
    
    // 检查文件状态是否仍然是 PENDING（可能被用户取消或删除）
    if (fileItem.uploadStatus !== UPLOAD_STATUS.PENDING) {
      continue
    }

    // 开始实际上传（不等待完成，让它们并发执行）
    startActualUpload(fileItem, index, isResume).catch(error => {
      // 错误已经在 handleUploadError 中处理，这里只是防止未捕获的异常
      logger.error('上传任务异常:', error)
      reportException(error, { action: 'upload_task_exception', description: `name=${fileItem?.name || ''}` })
    })
  }
}

// 开始实际上传
const startActualUpload = async (fileItem, index, isResume = false) => {
  // 更新文件状态为上传中
  fileItem.uploadStatus = UPLOAD_STATUS.UPLOADING
  fileItem.error = null
  fileItem.uploadSpeed = 0
  if (isResume) {
    // 续传时不显示「校验中」，进度归零后从实际上传再更新
    fileItem.isPreparing = false
    fileItem.progress = 0
    fileItem.uploadedSize = 0
  } else {
    // 新上传先进入「校验中」，等存完分片后再切到「上传中」和圆形进度条
    fileItem.isPreparing = true
    fileItem.progress = 0
  }
  await nextTick()

  await updateWakeLock()

  // 启动实时更新
  fileUtil.startSpeedUpdate(fileItem)

  try {
    if (isResume) {
      // 续传逻辑
      const response = {
        fileId: fileItem.fileId,
        uploadId: fileItem.uploadId,
        partInfoList: fileItem.partInfoList || null,
      }

      const result = await request.resumeUploadWithLatestUrls(
        response,
        { fileItem: fileItem },
        (progress) => {
          fileUtil.updateUploadProgress(progress, fileItem, fileUtil.updateUploadSpeed, progress === 0)
        },
        (chunkSizeBytes, durationMs) => {
          fileUtil.reportChunkUploadDone(fileItem, chunkSizeBytes, durationMs)
        },
        () => {
          return fileItem.uploadStatus === UPLOAD_STATUS.CANCELLED || fileItem.fileId === null
        }
      )

      await handleUploadResult(fileItem, result)
    } else {
      // 新上传逻辑
      if (!fileItem.file) {
        reportBehaviorLog({ action: 'upload_invalid_file', description: `name=${fileItem?.name || ''}` })
        throw new Error('文件对象不存在')
      }

      // 新上传时重置进度
      fileItem.progress = 0
      fileItem.uploadedSize = 0
      fileItem.fileId = null

      const result = await request.createFileWithFolders({
        fileItem: fileItem,
        parentFileId: parentFileId.value,
        accessToken: accessToken.value
      }, (progress) => {
        fileUtil.updateUploadProgress(progress, fileItem, fileUtil.updateUploadSpeed)
      }, (progress) => {
        fileUtil.updateUploadProgress(progress, fileItem, fileUtil.updateUploadSpeed, progress === 0)
      }, (chunkSizeBytes, durationMs) => {
        fileUtil.reportChunkUploadDone(fileItem, chunkSizeBytes, durationMs)
      }, () => {
        return fileItem.uploadStatus === UPLOAD_STATUS.CANCELLED || fileItem.fileId === null
      })

      await handleUploadResult(fileItem, result)
    }
  } catch (error) {
    await handleUploadError(fileItem, error)
  }
}

// 计算属性
const totalFileSize = computed(() => {
  return files.value.reduce((total, file) => total + file.size, 0)
})

// 计算剩余空间
const remainingSize = computed(() => {
  const maxTotalSizeBytes = fileUtil.parseFileSize(maxTotalSize.value)
  const remaining = maxTotalSizeBytes - totalFileSize.value
  return remaining > 0 ? remaining : 0
})

// 计算使用百分比
const usagePercentage = computed(() => {
  const maxTotalSizeBytes = fileUtil.parseFileSize(maxTotalSize.value)
  if (maxTotalSizeBytes === 0) {
    return 0
  }
  return Math.min((totalFileSize.value / maxTotalSizeBytes) * 100, 100)
})

// 计算已上传文件数量
const uploadedFilesCount = computed(() => {
  return files.value.filter(file => file.uploadStatus === UPLOAD_STATUS.SUCCESS && file.fileId).length
})


// 初始化参数
onMounted(async () => {
  // 清空本地存储
  // await uploadStorage.reset()
  // 优先从 sessionStorage 获取参数（按 code 区分）
  const storedCode = localStorage.getItem('lastAuthCode')
  const paramsKey = uploadStorage.getParamsKey()
  let storedParams = sessionStorage.getItem(paramsKey) || sessionStorage.getItem('uploadParams')

  // 解析并使用参数
  if (storedParams) {
    try {
      const params = JSON.parse(storedParams)

      // 设置参数
      if (params.items) {
        files.value = params.items
        // 初始化时获取已有文件的下载地址
        await fetchDownloadUrls(files.value)
      }

      // 从本地存储读取上传中的文件列表（只恢复完整数据，校验中断/刷新导致的不完整记录会删除）
      try {
        const allUploadData = await uploadStorage.getAllUploadData()
        const uploadingFiles = []

        for (const data of allUploadData) {
          if (!data.fileId || !data.uploadId || !data.partInfoList?.length) continue
          const complete = await uploadStorage.isUploadDataComplete(data.fileId)
          if (!complete) {
            await uploadStorage.deleteUploadData(data.fileId).catch(() => {})
            continue
          }
          uploadingFiles.push({
            id: ++fileIdCounter,
            name: data.fileInfo?.name || `文件_${data.fileId}`,
            size: data.fileInfo?.size || 0,
            type: data.fileInfo?.type || '',
            file: null,
            addedAt: data.fileInfo?.addedAt || new Date().toLocaleString(),
            downloadCount: data.fileInfo?.downloadCount || 0,
            uploadStatus: UPLOAD_STATUS.PAUSED,
            progress: data.fileInfo?.progress || 0,
            error: '文件对象已丢失，无法继续上传',
            uploadedSize: data.fileInfo?.uploadedSize || 0,
            uploadSpeed: 0,
            fileId: data.fileId,
          })
        }

        if (uploadingFiles.length > 0) {
          // 合并到文件列表（避免重复）
          uploadingFiles.forEach(uploadingFile => {
            const exists = files.value.find(f => f.fileId === uploadingFile.fileId)
            if (!exists) {
              files.value.unshift(uploadingFile)
              logger.info('从本地存储恢复上传中的文件:', uploadingFile.name, 'fileId:', uploadingFile.fileId)
            }
          })

          // 更新 fileIdCounter，确保不会重复
          const maxId = Math.max(...files.value.map(f => f.id || 0), 0)
          if (maxId > fileIdCounter) {
            fileIdCounter = maxId
          }
        }
      } catch (error) {
        logger.error('从本地存储读取上传中的文件失败:', error)
      }
      if (params.maxFileSize) maxFileSize.value = params.maxFileSize
      if (params.maxTotalSize) maxTotalSize.value = params.maxTotalSize
      if (params.allowMultiple !== undefined) allowMultiple.value = params.allowMultiple
      if (params.encryption !== undefined) {
        encryptionEnabled.value = params.encryption
        encryptedTransfer.value = params.encryption
      }
      if (params.description) pageDescription.value = params.description
      if (params.accessToken) accessToken.value = params.accessToken
      if (params.parentFileId) parentFileId.value = params.parentFileId
      if (params.shareLink) shareLink.value = params.shareLink
      if (params.expirationDate) expirationDate.value = params.expirationDate

      // 清除 sessionStorage 中的参数（按 code 清除，并兼容旧 key）
      sessionStorage.removeItem(paramsKey)
      sessionStorage.removeItem('uploadParams')
    } catch (error) {
      logger.error('解析 sessionStorage 参数失败:', error)

      showDialog({
        title: '解析参数失败',
        message: "请联系商家处理！",
        type: 'error'
      })
      router.push('/upload')
      return
    }
  } else {
    router.push('/upload')
    return
  }

  // 配置请求基础URL和认证信息
  if (accessToken.value) {
    request.setDefaultHeaders({
      'Authorization': accessToken.value
    })
  }

  // 延迟启动引导，确保页面完全渲染
  await nextTick()
  setTimeout(() => {
    startTourGuide()
  }, 800)

  // 页面切到后台时释放 Wake Lock（规范要求），再次可见且仍有上传时重新请求
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      releaseWakeLock()
    } else if (document.visibilityState === 'visible' && hasActiveUpload()) {
      requestWakeLock()
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  visibilityCleanup = () => document.removeEventListener('visibilitychange', onVisibilityChange)
})

// 初始化引导
const initTourGuide = () => {
  // 确保所有按钮元素都已渲染

  // 创建 Driver 实例
  driverInstance = driver({
    allowClose: true,
    // driver.js 1.x 使用 overlayClickBehavior：传空函数使点击遮罩无效果，仅通过关闭按钮或完成引导关闭
    overlayClickBehavior: () => {},
    closeBtnText: '✕',
    nextBtnText: '下一步',
    prevBtnText: '上一步',
    doneBtnText: '完成',
    stageBackground: '#ffffff',
    showProgress: true,
    allowKeyboardControl: false,
    disableActiveInteraction: true, // 高亮的目标按钮不可点击，避免误触
    popoverClass: 'driverjs-theme',
    onHighlightStarted: (element) => {
      // 高亮开始时滚动到目标元素
      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    onDestroyStarted: () => {
      // 引导销毁开始（用户关闭或完成）
      localStorage.setItem('upload-tour-completed', 'true')
      logger.info('引导完成')
      driverInstance.destroy()
      // 引导完成后滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  })

  // 定义引导步骤
  driverInstance.setSteps([
    {
      element: '.add-file-btn',
      popover: {
        title: '上传文件',
        description: '点击右上角的"+"按钮，选择要上传的文件。您也可以直接拖拽文件到上传区域。',
        side: 'bottom',
        align: 'center'
      }
    },
    {
      element: '.start-upload-btn',
      popover: {
        title: '开始上传',
        description: '选择文件后，点击"开始上传任务"按钮开始上传文件。您可以同时上传多个文件。',
        side: 'top',
        align: 'center'
      }
    },
    {
      element: '.copy-btn',
      popover: {
        title: '复制分享链接',
        description: '文件上传成功后，系统会自动生成分享链接。点击"复制"按钮即可复制链接，分享给他人。',
        side: 'top',
        align: 'center'
      }
    },
    {
      element: '.email-btn',
      popover: {
        title: '复制到邮件正文',
        description: '点击"复制到邮件正文"按钮，系统会自动生成邮件正文内容，在邮件正文中粘贴即可，可以通过邮件发送给他人。',
        side: 'top',
        align: 'center'
      }
    },
    {
      element: '.help-link',
      popover: {
        title: '查看教程',
        description: '如果还有疑问，可以点击"查看教程"按钮查看详细的使用说明。',
        side: 'top',
        align: 'center'
      }
    }
  ])

  return true
}

// 启动引导
const startTourGuide = () => {
  // 检查是否已完成引导
  const isCompleted = localStorage.getItem('upload-tour-completed') === 'true'
  if (isCompleted) {
    return
  }

  // 如果 driver 未初始化，先初始化
  if (!driverInstance) {
    const initialized = initTourGuide()
    if (!initialized) {
      return
    }
  }

  // 启动引导
  if (driverInstance) {
    driverInstance.drive()
  }
}

// 组件卸载时清理定时器和 Wake Lock
onUnmounted(() => {
  if (dragOverTimer) {
    clearTimeout(dragOverTimer)
    dragOverTimer = null
  }
  if (visibilityCleanup) {
    visibilityCleanup()
    visibilityCleanup = null
  }
  releaseWakeLock()
})

// 触发 vue-upload-component 选择文件
const triggerUpload = () => {
  const upload = uploadRef.value
  if (upload?.$refs?.input) {
    upload.$refs.input.click()
  }
}

// 处理单个文件添加（通用函数，支持 vue-upload-component 选择与拖拽）
const processSingleFile = (nativeFile, uploadComponentFile = null) => {
  if (!nativeFile) {
    logger.error('无法获取文件对象')
    showDialog({
      title: '无法获取文件对象',
      message: '请检查文件是否存在',
      type: 'error'
    })
    return
  }

  // 确保是 File 对象
  if (!(nativeFile instanceof File)) {
    logger.error('文件对象类型不正确:', typeof nativeFile)
    showDialog({
      title: '文件对象类型不正确',
      message: '请检查文件是否存在',
      type: 'error'
    })
    return
  }

  // 在 processSingleFile 中，File 类型检查之后、checkFileNamesAndSizes 之前添加：
  if (nativeFile.size <= 0) {
    showDialog({
      title: '无效文件',
      message: `文件 "${nativeFile.name}" 大小为 0B，请检查文件是否正常。`,
      type: 'warning'
    })
    return
  }

  // 使用 checkFileNamesAndSizes 进行完整检查
  const maxTotalSizeBytes = fileUtil.parseFileSize(maxTotalSize.value)
  const maxFileSizeBytes = fileUtil.parseFileSize(maxFileSize.value)

  const checkedFiles = fileUtil.checkFileNamesAndSizes([nativeFile], files.value, {
    allowMultiple: allowMultiple.value,
    maxTotalSizeBytes: maxTotalSizeBytes,
    maxFileSizeBytes: maxFileSizeBytes,
    onError: (title, message, type) => {
      showDialog({
        title,
        message,
        type: type
      })
    }
  })

  if (checkedFiles.length > 0) {
    const checkedFile = checkedFiles[0]

    const fileItem = {
      id: ++fileIdCounter,
      name: checkedFile.name,
      size: checkedFile.size,
      type: checkedFile.type,
      file: checkedFile.file,
      addedAt: new Date().toLocaleString(),
      downloadCount: 0,
      uploadStatus: UPLOAD_STATUS.PROCESSING, // 临时状态：处理中，不可上传
      progress: 0,
      error: null,
      uploadedSize: 0,
      uploadSpeed: 0,
      pondFile: uploadComponentFile // 兼容：vue-upload-component 不传，拖拽为 null
    }

    // 立即添加到列表，确保用户能看到
    files.value.unshift(fileItem)
    logger.info('文件已添加到列表（处理中）:', checkedFile.name)
    logger.info('fileItem:', fileItem)

    // 直接处理文件
    handleFileProcessing(fileItem)
  }
}

// vue-upload-component 的 input-filter：在文件加入组件前拦截，交给自定义流程并阻止组件内部列表
const handleInputFilter = (newFile, oldFile, prevent) => {
  if (newFile && !oldFile && newFile.file) {
    processSingleFile(newFile.file, null)
    return prevent()
  }
}

// 批量获取文件的下载地址
const fetchDownloadUrls = async (fileList) => {
  if (!fileList || fileList.length === 0) {
    return
  }

  // 过滤出需要获取下载地址的文件（有 fileId 但没有 downloadUrl 的）
  const filesNeedUrl = fileList.filter(file =>
    file.fileId && (!file.downloadUrl || file.downloadUrl === '')
  )

  if (filesNeedUrl.length === 0) {
    return
  }

  // 并发获取下载地址（限制并发数，避免过多请求）
  const batchSize = 5 // 每批处理5个文件
  for (let i = 0; i < filesNeedUrl.length; i += batchSize) {
    const batch = filesNeedUrl.slice(i, i + batchSize)
    await Promise.all(
      batch.map(async (file) => {
        try {
          const downloadUrl = await request.getFileDownloadUrl(file)
          if (downloadUrl && downloadUrl !== '') {
            file.downloadUrl = downloadUrl
          }
        } catch (error) {
          logger.error(`获取文件 "${file.name}" 的下载地址失败:`, error)
        }
      })
    )
  }
}

// 处理所有文件处理完成
const handleFileProcessing = async (fileItem) => {
  // 获取已存在的文件（不包括处理中的文件）
  const existingFiles = files.value.filter(f =>
    f.uploadStatus !== UPLOAD_STATUS.PROCESSING &&
    f.uploadStatus !== -1
  )

  const processedFile = await fileUtil.checkFileContent(fileItem, existingFiles, {
    onWarning: (title, message) => {
      showDialog({
        title,
        message,
        type: 'warning'
      })
    },
    getLogger: () => logger
  })
  if (processedFile && processedFile.name !== fileItem.name) {
    fileItem.name = processedFile.name
  }

  // 找到文件在数组中的索引
  const fileIndex = files.value.findIndex(f => f.id === fileItem.id)

  if (fileIndex === -1) {
    logger.warn('文件不在列表中，无法更新:', fileItem.name)
    return
  }

  if (processedFile) {
    // 文件通过检查（使用文件名和大小对比）
    // 通过索引直接更新数组中的对象，确保响应式更新
    files.value[fileIndex] = {
      ...files.value[fileIndex],
      name: processedFile.name, // 使用处理后的文件名（可能已重命名）
      uploadStatus: UPLOAD_STATUS.WAITING,
      pondFile: null
    }

    // 确保响应式更新UI
    await nextTick()
  } else {
    // 文件被跳过（重复）
    files.value.splice(fileIndex, 1)
    // 确保响应式更新UI
    await nextTick()
  }
}

// 拖拽处理
const handleDragOver = (e) => {
  e.preventDefault()
  e.stopPropagation()

  // 如果已经是拖拽状态，不重复设置定时器（因为 handleDragOver 会持续触发）
  if (isDragOver.value) {
    return
  }

  isDragOver.value = true

  // 只在第一次进入拖拽状态时设置 5 秒后自动关闭拖拽状态
  dragOverTimer = setTimeout(() => {
    isDragOver.value = false
    dragOverTimer = null
  }, 5000)
}

const handleDragLeave = (e) => {
  e.preventDefault()
  e.stopPropagation()
  // 只有当离开整个拖拽区域时才设置为 false
  if (!e.currentTarget.contains(e.relatedTarget)) {
    // 清除定时器
    if (dragOverTimer) {
      clearTimeout(dragOverTimer)
      dragOverTimer = null
    }
    isDragOver.value = false
  }
}

const handleDrop = (e) => {
  e.preventDefault()
  e.stopPropagation()

  // 清除定时器
  if (dragOverTimer) {
    clearTimeout(dragOverTimer)
    dragOverTimer = null
  }

  isDragOver.value = false

  // 只接受文件，过滤掉文件夹（拖入文件夹不处理）
  let droppedFiles = []
  let hasDroppedFolder = false
  if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
    for (const item of e.dataTransfer.items) {
      const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null
      if (entry && entry.isDirectory) {
        hasDroppedFolder = true
        continue
      }
      const file = item.getAsFile()
      if (file && file instanceof File) {
        droppedFiles.push(file)
      }
    }
  } else {
    droppedFiles = Array.from(e.dataTransfer.files || [])
  }

  if (hasDroppedFolder) {
    showDialog({
      title: '仅支持文件',
      message: '不支持拖入文件夹，请压缩后再添加。',
      type: 'warning'
    })
  }

  droppedFiles.forEach(file => {
    processSingleFile(file, null) // 拖拽的文件没有 FilePond 对象
  })
}


// 单独上传文件
const uploadSingleFile = async (fileItem, index) => {
  if (!fileItem.file && fileItem.uploadStatus !== UPLOAD_STATUS.PAUSED) return

  if (fileItem.uploadStatus === UPLOAD_STATUS.PROCESSING) {
    return
  }

  reportBehaviorLog({
    action: 'upload_single_click',
    description: `name=${fileItem?.name || ''}, size=${fileItem?.size || 0}`
  })

  // 先设置为待上传状态
  fileItem.uploadStatus = UPLOAD_STATUS.PENDING
  fileItem.error = null
  await nextTick()

  // 检查当前正在上传的文件数量
  const activeCount = getActiveUploadCount()
  
  if (activeCount < concurrentUploads.value) {
    // 如果未达到上限，直接开始上传（不等待完成，让它们并发执行）
    startActualUpload(fileItem, index, false).catch(error => {
      // 错误已经在 handleUploadError 中处理，这里只是防止未捕获的异常
      logger.error('上传任务异常:', error)
    })
  } else {
    // 如果已达到上限，加入待上传队列
    pendingUploadQueue.push({ fileItem, index, isResume: false })
  }
}

// 暂停单个文件上传
const pauseSingleUpload = async (fileItem, index) => {
  if (fileItem && fileItem.uploadStatus === UPLOAD_STATUS.UPLOADING) {
    reportBehaviorLog({
      action: 'upload_pause',
      description: `name=${fileItem?.name || ''}, progress=${fileItem?.progress || 0}`
    })
    fileItem.uploadStatus = UPLOAD_STATUS.PAUSED
    fileItem.uploadSpeed = 0
    fileUtil.stopSpeedUpdate(fileItem.id, { deleteTracker: false })
    logger.info('文件已暂停:', fileItem.name, '进度:', fileItem.progress)
    await updateWakeLock()
    // 暂停后，处理待上传队列，让下一个文件开始上传
    await processPendingQueue()
  }
}

// 处理上传结果
const handleUploadResult = async (fileItem, result) => {
  // 如果是暂停，上传数据已经在 request.js 中保存到本地存储
  if (!result.success && result.error === "PAUSED") {
    await updateWakeLock()
    return // 暂停时直接返回，不显示错误
  }

  if (result.success) {
    reportBehaviorLog({
      action: 'upload_success',
      description: `name=${fileItem?.name || ''}, size=${fileItem?.size || 0}`
    })
    // 处理成功情况
    if (result.fileId) {
      fileItem.fileId = result.fileId
    }

    // 上传成功
    fileItem.uploadStatus = UPLOAD_STATUS.SUCCESS
    fileItem.progress = 100
    fileItem.uploadedSize = fileItem.size
    fileItem.uploadSpeed = 0
    fileUtil.stopSpeedUpdate(fileItem.id)
    await updateWakeLock()
    // 文件上传完成后，处理待上传队列
    await processPendingQueue()

    // 上传成功（本地存储的上传数据已在 request.js 中删除）

    if (fileItem.uploadStatus !== UPLOAD_STATUS.CANCELLED) {
      // 如果是批量上传，不显示单个文件的成功提示
      if (!fileItem._isBatchUpload) {
        showDialog({
          title: '上传成功',
          message: `文件 "${fileItem.name}" 上传完成`,
          type: 'success'
        })
      }
      
      // 清除批量上传标志
      fileItem._isBatchUpload = false

      // 上传成功后查询文件列表
      try {
        const uploadParams = {
          parentFileId: parentFileId.value,
          items: files.value
        }
        const listResult = await request.getListData(uploadParams)
        if (listResult.success && listResult.data && listResult.data.items) {
          // 获取服务器返回的文件列表
          const serverItems = listResult.data.items

          // 获取本地正在上传或等待上传的文件（这些文件不在服务器列表中）
          // 排除 SUCCESS（已成功）状态，因为已成功的文件应该在服务器列表中
          const localPendingFiles = files.value.filter(file =>
            file.uploadStatus != null && file.uploadStatus !== UPLOAD_STATUS.SUCCESS
          )

          // 创建新的文件列表：先添加本地待上传的文件，再添加服务器返回的文件
          const newFilesList = []

          // 1. 先添加本地待上传的文件（保留这些文件的状态，显示在顶部）
          localPendingFiles.forEach(localFile => {
            newFilesList.push(localFile)
          })

          // 2. 添加服务器返回的文件，并更新已上传文件的下载次数等信息
          serverItems.forEach(serverFile => {
            const existingFile = files.value.find(localFile =>
              localFile.fileId === serverFile.fileId
            )

            if (existingFile) {
              // 如果本地已有该文件，保留本地信息（如下载次数、id等），更新服务器信息
              newFilesList.push({
                id: existingFile.id || ++fileIdCounter,
                name: serverFile.name,
                size: serverFile.size,
                fileId: serverFile.fileId,
                downloadCount: existingFile.downloadCount || 0,
                downloadUrl: existingFile.downloadUrl || '',
                uploadStatus: UPLOAD_STATUS.SUCCESS,
                progress: 100,
                uploadedSize: serverFile.size || 0,
                uploadSpeed: 0,
                addedAt: existingFile.addedAt || new Date().toLocaleString(),
                type: existingFile.type || '',
                file: existingFile.file || null
              })
            } else {
              // 如果本地没有该文件，添加服务器文件（可能是其他方式上传的）
              newFilesList.push({
                id: ++fileIdCounter,
                name: serverFile.name,
                size: serverFile.size,
                fileId: serverFile.fileId,
                downloadCount: 0,
                downloadUrl: '',
                uploadStatus: UPLOAD_STATUS.SUCCESS,
                progress: 100,
                uploadedSize: serverFile.size || 0,
                uploadSpeed: 0,
                addedAt: new Date().toLocaleString(),
                type: '',
                file: null,
                error: null
              })
            }
          })

          // 3. 更新文件列表
          files.value = newFilesList
          logger.info('newFilesList:', newFilesList)

          // 4. 更新文件列表后，批量获取下载地址
          await fetchDownloadUrls(files.value)
        }
      } catch (error) {
        logger.error('获取文件列表失败:', error)
        reportException(error, { action: 'upload_refresh_list_fail', description: `name=${fileItem?.name || ''}` })
        // 不显示错误提示，避免影响用户体验
      }
    }
  } else {
    // 处理失败情况
    logger.error('上传失败:', result.error)
    reportBehaviorLog({
      action: 'upload_fail',
      description: `name=${fileItem?.name || ''}, error=${result?.error || ''}`
    })
    throw new Error(result.error || '上传失败')
  }
}

// 处理上传错误
const handleUploadError = async (fileItem, error) => {
  logger.error('上传失败:', error)
  reportException(error, {
    action: 'upload_error',
    description: `name=${fileItem?.name || ''}`
  })

  if (fileItem.uploadStatus === UPLOAD_STATUS.CANCELLED) {
    return
  }

  // 如果是暂停，不显示错误，保持暂停状态（不删 tracker，续传时速度连续）
  if (fileItem.uploadStatus === UPLOAD_STATUS.PAUSED) {
    fileItem.uploadSpeed = 0
    fileUtil.stopSpeedUpdate(fileItem.id, { deleteTracker: false })
    await updateWakeLock()
    return
  }

  // 如果未取消，则设置为失败状态
  fileItem.uploadStatus = UPLOAD_STATUS.ERROR
  fileItem.error = error.message || '上传失败'

  fileItem.progress = 0
  fileItem.uploadedSize = 0
  fileItem.uploadSpeed = 0
  fileUtil.stopSpeedUpdate(fileItem.id)
  await updateWakeLock()

  // 如果是批量上传，不显示单个文件的失败提示
  if (!fileItem._isBatchUpload) {
    showDialog({
      title: '上传失败',
      message: `文件 "${fileItem.name}" 上传失败: ${error.message}`,
      type: 'error'
    })
  }
  
  // 清除批量上传标志
  fileItem._isBatchUpload = false

  // 文件上传失败后，处理待上传队列
  await processPendingQueue()
}

// 继续单个文件上传
const resumeSingleUpload = async (fileItem, index) => {
  logger.info('resumeSingleUpload:', fileItem)
  logger.info('index:', index)
  if (!fileItem) return

  if (fileItem.uploadStatus === UPLOAD_STATUS.PAUSED) {
    reportBehaviorLog({
      action: 'upload_resume',
      description: `name=${fileItem?.name || ''}, progress=${fileItem?.progress || 0}`
    })
    // 检查是否有 fileId，如果没有则按新上传处理
    if (!fileItem.fileId) {
      logger.warn('继续上传失败: 未找到 fileId，按新上传处理:', fileItem.name)
      await uploadSingleFile(fileItem, index)
      return
    }

    // 先设置为待上传状态
    fileItem.uploadStatus = UPLOAD_STATUS.PENDING
    fileItem.error = null
    await nextTick()

    // 检查当前正在上传的文件数量
    const activeCount = getActiveUploadCount()
    
    if (activeCount < concurrentUploads.value) {
      // 如果未达到上限，直接开始上传（不等待完成，让它们并发执行）
      startActualUpload(fileItem, index, true).catch(error => {
        // 错误已经在 handleUploadError 中处理，这里只是防止未捕获的异常
        logger.error('上传任务异常:', error)
      })
    } else {
      // 如果已达到上限，加入待上传队列
      pendingUploadQueue.push({ fileItem, index, isResume: true })
    }
  }
}

// 取消单个文件上传
const cancelSingleUpload = async (fileItem, index) => {
  if (fileItem) {
    reportBehaviorLog({
      action: 'upload_cancel',
      description: `name=${fileItem?.name || ''}, progress=${fileItem?.progress || 0}`
    })
    // 如果文件在待上传队列中，从队列中移除
    const queueIndex = pendingUploadQueue.findIndex(item => item.fileItem.id === fileItem.id)
    if (queueIndex > -1) {
      pendingUploadQueue.splice(queueIndex, 1)
    }

    fileItem.uploadStatus = UPLOAD_STATUS.CANCELLED
    fileItem.progress = 0
    fileItem.error = '取消上传'
    fileItem.uploadedSize = 0
    fileItem.uploadSpeed = 0
    
    // 删除本地存储的上传数据（含校验中已创建的任务，避免残留不完整数据）
    if (fileItem.fileId) {
      fileItem.uploadId = null
      fileItem.partInfoList = null
      try {
        await uploadStorage.deleteUploadData(fileItem.fileId)
      } catch (err) {
        logger.error('删除本地存储数据失败:', err)
      }
      fileItem.fileId = null
    }
    fileUtil.stopSpeedUpdate(fileItem.id)
    await updateWakeLock()
    // 取消后，如果有待上传队列，处理下一个
    await processPendingQueue()

    // 刷新后丢失file对象，直接从列表移除
    if (!fileItem.file){
      removeFile(index)
    }
  }
}

// 下载文件
const downloadFile = async (fileItem, index) => {
  try {
    logger.debug('下载文件:', fileItem)
    if (!fileItem.fileId) {
      throw new Error('数据异常，暂无法下载')
      return
    }

    // 立即显示"准备中..."状态，提升用户体验
    fileItem.downloadStatus = 'preparing'

    // 先检查是否已有保存的下载URL
    let downloadUrl = fileItem.downloadUrl

    // 如果没有保存的URL，才请求获取
    if (!downloadUrl || downloadUrl === "") {
      downloadUrl = await request.getFileDownloadUrl(fileItem)

      if (!downloadUrl || downloadUrl === "") {
        fileItem.downloadStatus = null
        throw new Error('下载URL为空')
      }

      // 保存下载URL到文件对象，避免下次重复请求
      fileItem.downloadUrl = downloadUrl
    }

    // 增加下载次数
    fileItem.downloadCount = (fileItem.downloadCount || 0) + 1

    const result = await request.downloadFile(downloadUrl, fileItem.name)

    // logger.info('result:', result)
    if (!result.success) {
      fileItem.downloadStatus = null
      throw new Error(result.error || '下载失败')
    }

    // 延迟清除状态，让用户看到"准备中..."提示
    setTimeout(() => {
      fileItem.downloadStatus = null
    }, 1000)
  } catch (error) {
    logger.error('下载失败:', error)
    reportException(error, { action: 'download_error', description: `name=${fileItem?.name || ''}` })
    fileItem.downloadStatus = null
    showDialog({
      title: '下载失败',
      message: `文件 "${fileItem.name}" 下载失败: ${error.message}`,
      type: 'error'
    })
  }
}
// 删除文件
const removeFile = (index) => {
  files.value.splice(index, 1)
}

// 切换加密传输
const toggleEncryption = () => {
  encryptedTransfer.value = !encryptedTransfer.value
}


// 开始上传
const startUpload = async () => {
  if (files.value.length === 0) return

  // 统计待上传文件数量（包括等待上传、上传失败和暂停的文件）
  const pendingFiles = files.value.filter(file =>
    file.uploadStatus === UPLOAD_STATUS.WAITING ||
    file.uploadStatus === UPLOAD_STATUS.ERROR ||
    file.uploadStatus === UPLOAD_STATUS.PAUSED
  )
  const pendingCount = pendingFiles.length

  if (pendingCount === 0) {
    showDialog({
      title: '提示',
      message: '没有待上传的文件',
      type: 'info'
    })
    return
  }

  uploading.value = true

  try {
    const batchUploadFileIds = new Set() // 记录批量上传的文件ID

    // 逐个上传文件（复用单文件上传逻辑）
    for (let i = 0; i < pendingFiles.length; i++) {
      const fileItem = pendingFiles[i]

      // 批量上传时跳过已完成的文件
      if (fileItem.uploadStatus === UPLOAD_STATUS.SUCCESS || fileItem.progress === 100) {
        continue
      }
      
      // 标记为批量上传的文件
      fileItem._isBatchUpload = true
      batchUploadFileIds.add(fileItem.id)
      
      try {
        // 如果状态为暂停，调用续传方法
        if (fileItem.uploadStatus === UPLOAD_STATUS.PAUSED) {
          resumeSingleUpload(fileItem, i).catch(error => {
            logger.error('续传失败:', error)
          })
        } else {
          // 其他状态（WAITING、ERROR）调用新上传方法
          uploadSingleFile(fileItem, i).catch(error => {
            logger.error('上传失败:', error)
          })
        }
      } catch (e) {
        logger.error('启动上传失败:', e)
      }
    }

    // 等待所有批量上传的文件完成（成功、失败、暂停或取消）
    // 使用轮询方式检查，直到所有批量上传的文件都完成
    const checkBatchUploadComplete = () => {
      const batchUploadFiles = files.value.filter(f => batchUploadFileIds.has(f.id))
      const stillActive = batchUploadFiles.some(f => 
        f.uploadStatus === UPLOAD_STATUS.UPLOADING || 
        f.uploadStatus === UPLOAD_STATUS.PENDING
      )
      
      // 检查待上传队列中是否还有批量上传的文件
      const queueHasBatchUpload = pendingUploadQueue.some(item => 
        batchUploadFileIds.has(item.fileItem.id)
      )
      
      return !stillActive && !queueHasBatchUpload
    }

    // 轮询检查，直到所有批量上传的文件都完成
    while (!checkBatchUploadComplete()) {
      await new Promise(resolve => setTimeout(resolve, 200))
    }

    uploading.value = false

    // 统计批量上传的结果
    const batchUploadFiles = files.value.filter(f => batchUploadFileIds.has(f.id))
    const successCount = batchUploadFiles.filter(f => f.uploadStatus === UPLOAD_STATUS.SUCCESS).length
    const failCount = batchUploadFiles.filter(f => f.uploadStatus === UPLOAD_STATUS.ERROR).length
    const pausedCount = batchUploadFiles.filter(f => f.uploadStatus === UPLOAD_STATUS.PAUSED).length

    // 只有当有文件完成（成功或失败）时才显示结果
    if (successCount > 0 || failCount > 0) {
      if (failCount > 0) {
        showDialog({
          title: '部分上传成功',
          message: `成功上传 ${successCount} 个文件，${failCount} 个文件上传失败${pausedCount > 0 ? `，${pausedCount} 个文件已暂停` : ''}`,
          type: 'warning'
        })
      } else {
        showDialog({
          title: '上传成功',
          message: `共 ${successCount} 个文件上传完成！${pausedCount > 0 ? `，${pausedCount} 个文件已暂停` : ''}`,
          type: 'success'
        })
      }
    }

  } catch (error) {
    uploading.value = false
    logger.error('上传过程中发生错误:', error)
    reportException(error, { action: 'batch_upload_exception' })
    showDialog({
      title: '上传错误',
      message: `上传过程中发生错误: ${error.message}`,
      type: 'error'
    })
  }

  // 结束后要遍历所有fileItem的_isBatchUpload标志位，如果为true，则设置为false
  files.value.forEach(fileItem => {
    fileItem._isBatchUpload = false
  })
}

// 复制分享链接
const copyShareLink = async () => {
  if (!shareLink.value) return

  const copied = await copyToClipboard(shareLink.value)
  if (copied) {
    reportBehaviorLog({
      action: 'share_copy',
      description: '复制分享链接'
    })
    copySuccess.value = true
    setTimeout(() => {
      copySuccess.value = false
    }, 2000)
  } else {
    reportBehaviorLog({
      action: 'share_copy_failed',
      description: '复制分享链接失败'
    })
    showDialog({
      title: '复制失败',
      message: '请长按下方链接手动复制：\n\n' + shareLink.value,
      type: 'warning'
    })
  }
}

// 复制到邮件正文
const copyToEmail = async () => {
  // 获取已上传的文件列表
  const uploadedFiles = files.value.filter(file => file.uploadStatus === UPLOAD_STATUS.SUCCESS && file.fileId)

  reportBehaviorLog({
    action: 'share_copy_email',
    description: `复制到邮件正文, files=${uploadedFiles.length}`
  })

  const result = await copyToEmailUtil(uploadedFiles, shareLink.value, expirationDate.value)

  if (result.success) {
    showDialog({
      title: '复制成功',
      message: result.message,
      type: 'success'
    })
  } else {
    showDialog({
      title: '提示',
      message: result.message,
      type: 'warning'
    })
  }
}

// 显示教程
const showTutorial = () => {
  showDialog({
    title: '使用教程',
    message: '1. 上传文件：点击右上角的"+"按钮，选择要上传的文件。您也可以直接拖拽文件到上传区域。\n\n2. 开始上传：选择文件后，点击"开始上传任务"按钮开始上传文件。您可以同时上传多个文件。\n\n3. 复制分享链接：文件上传成功后，系统会自动生成分享链接。点击"复制"按钮即可复制链接，分享给他人。\n\n4. 复制到邮件正文：点击"复制到邮件正文"按钮，系统会自动生成邮件正文内容，在邮件正文中粘贴即可，可以通过邮件发送给他人。',
    type: 'info',
    width: '520px'
  })
}
</script>

<style scoped>
/* 隐藏 vue-upload-component 的默认界面，仅用其 input 触发选择 */
.hidden-upload {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.upload-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 全局拖拽指示器 */
.global-drag-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease-in-out;
}

.global-drag-content {
  background: white;
  border-radius: 20px;
  padding: 3rem 4rem;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.3s ease-out;
}

.global-drag-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: bounce 1s ease-in-out infinite;
}

.global-drag-text {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-10px);
  }

  60% {
    transform: translateY(-5px);
  }
}


.upload-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.upload-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
}

.upload-tip {
  margin-top: 1rem;
  text-align: center;
  width: 100%;
  max-width: 600px;
}

.tip-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

.tip-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  animation: shake 1s ease-in-out infinite;
}

.tip-text {
  color: #856404;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
  }
  50% {
    box-shadow: 0 2px 12px rgba(255, 193, 7, 0.5);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-2px);
  }
  75% {
    transform: translateX(2px);
  }
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.upload-header h1 {
  margin: 0;
  color: #333;
  font-size: 1.5rem;
}

.add-file-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  padding: 0;
}

.add-icon {
  width: 100%;
  height: 100%;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
  margin-bottom: 0.5rem;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}


/* 文件列表样式 */
.upload-file-list {
  width: 100%;
  border-radius: 12px;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
  height: 300px;
}

.file-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.file-count {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.add-more-btn {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-more-btn:hover {
  background: #218838;
  transform: translateY(-1px);
}

.upload-file-list .file-list-content {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.upload-file-list .file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
  box-sizing: border-box;
}

.upload-file-list .file-item:last-child {
  margin-bottom: 0;
}


.upload-area:hover,
.upload-area.drag-over {
  border-color: #007bff;
  background: #f0f8ff;
}

.upload-icon {
  position: relative;
  display: inline-block;
  margin-bottom: 0.75rem;
}

.folder-icon {
  font-size: 4rem;
  opacity: 0.6;
  color: #007bff;
}

.plus-icon {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
}

.upload-text {
  font-size: 16px;
  color: #666;
  margin: 0;
}


.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.status-item:last-child {
  border-bottom: none;
}

.status-label {
  color: #333;
  font-size: 14px;
}

.status-value {
  color: #666;
  font-size: 14px;
}

.concurrent-radio-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.radio-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.radio-option input[type="radio"] {
  margin: 0;
  margin-right: 0.25rem;
  cursor: pointer;
  width: 16px;
  height: 16px;
  accent-color: #007bff;
}

.radio-option span {
  font-size: 14px;
  color: #333;
  white-space: nowrap;
}

.radio-option input[type="radio"]:checked + span {
  color: #007bff;
  font-weight: 500;
}

.status-value.warning {
  color: #f39c12;
  font-weight: 500;
}

.status-value.danger {
  color: #e74c3c;
  font-weight: 600;
}

.progress-item {
  padding: 0.5rem 0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #007bff;
  transition: width 0.3s ease, background-color 0.3s ease;
  border-radius: 4px;
}

.progress-fill.warning {
  background: #f39c12;
}

.progress-fill.danger {
  background: #e74c3c;
}

.toggle-switch {
  width: 44px;
  height: 24px;
  background: #ddd;
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s;
}

.toggle-switch.active {
  background: #007bff;
}

.toggle-slider {
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
}

.toggle-switch.active .toggle-slider {
  transform: translateX(20px);
}

.share-link-item {
  align-items: flex-start;
}

.share-link-item .status-label {
  margin-top: 0.25rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.share-actions-item {
  align-items: center;
  padding-top: 0.25rem;
}

.share-actions-item .status-label {
  display: none;
}

.share-content {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex: 1;
  min-width: 0;
  justify-content: flex-end;
}

.share-text {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 80%;
  padding: 0;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  text-align: right;
}

.copy-btn {
  padding: 0.25rem 0;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 60px;
}

.copy-btn:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.copy-btn:disabled {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.share-actions {
  display: flex;
  align-items: center;
  flex-direction: row;
}

.email-btn {
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.email-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #dee2e6;
}

.email-btn:disabled {
  background: #f8f9fa;
  color: #adb5bd;
  border-color: #e9ecef;
  cursor: not-allowed;
}

.help-text {
  color: #6c757d;
  font-size: 14px;
  margin-left: 1rem;
}

.help-link {
  color: #007bff;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s;
}

.help-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.file-list-content {
  display: flex;
  flex-direction: column;
}

.file-list-content::-webkit-scrollbar {
  width: 6px;
}

.file-list-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.file-list-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.file-list-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: white;
  transition: background-color 0.2s;
  min-height: 2.5rem;
}

.file-item:last-child {
  border-bottom: none;
}

.file-item:hover {
  background: #f8f9fa;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding-right: 1rem;
}

.file-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.file-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
}

.file-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  width: 100%;
  max-width: 100%;
}

.file-size {
  font-size: 12px;
  color: #666;
  font-weight: normal;
}

.file-size .remaining-time {
  margin-left: 6px;
  color: #888;
}

.file-name-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* 圆形进度条样式 */
.circular-progress-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.progress-text {
  font-size: 12px;
  color: #333;
}

.file-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.status-text {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-icon {
  width: 14px;
  height: 14px;
}

.status-waiting {
  color: #6c757d;
}

.status-pending {
  color: #ff9800;
}

.status-preparing {
  color: #ff9800;
}

.status-uploading {
  color: #007bff;
}

/* 校验中文案后的动态省略号，三点依次闪烁 */
.preparing-text {
  color: #6c757d;
}
.preparing-dots .dot {
  display: inline-block;
  animation: preparing-dot 1s ease-in-out infinite;
  opacity: 0.35;
}
.preparing-dots .dot:nth-child(2) {
  animation-delay: 0.15s;
}
.preparing-dots .dot:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes preparing-dot {
  0%, 100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}

.status-success {
  color: #28a745;
}

.status-error {
  color: #dc3545;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #218838;
  transform: translateY(-1px);
}

.download-count {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 11px;
  color: #666;
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.download-icon {
  font-size: 10px;
}

.count-text {
  font-weight: 600;
  color: #495057;
}

.btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-icon {
  width: 16px;
  height: 16px;
}

.btn-icon-only {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #666;
}

.btn-icon-only:hover {
  background: #f8f9fa;
  transform: scale(1.1);
}

.btn-icon-only:active {
  transform: scale(0.95);
}

.btn-icon-only:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon-only:disabled:hover {
  background: none;
  transform: none;
}

.download-btn-with-badge {
  position: relative;
}

.download-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  padding: 0 4px;
  background-color: #f56c6c;
  color: #fff;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  box-shadow: 0 0 0 1px #fff;
  z-index: 1;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
  transform: translateY(-1px);
}

.upload-actions {
  text-align: center;
}

.start-upload-btn {
  width: 100%;
  padding: 0.5rem 2rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.start-upload-btn:hover:not(:disabled) {
  background: #0056b3;
}

.start-upload-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.upload-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.cancel-upload-btn {
  padding: 0.75rem 1.5rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-upload-btn:hover {
  background: #c82333;
}

.upload-status {
  color: #666;
  font-size: 14px;
}

/* 旧的进度条样式已移除，使用圆形进度条 */

.upload-status.success {
  color: #28a745;
  font-size: 12px;
  margin-top: 0.25rem;
}

.upload-status.error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 0.25rem;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover {
  background: #e0a800;
}

@media (max-width: 768px) {
  .upload-container {
    padding: 1rem;
  }

  .upload-card {
    padding: 1rem;
  }

  .upload-area {
    padding: 1.5rem 1rem;
  }

  .file-list {
    max-height: 120px;
  }

  .file-item {
    padding: 0.4rem 0.75rem;
    min-height: 2rem;
  }

  .file-actions {
    gap: 0.4rem;
  }

  .btn-icon {
    width: 14px;
    height: 14px;
  }

  .btn-icon-only {
    padding: 0.2rem;
    font-size: 11px;
  }

  .status-text {
    font-size: 11px;
  }

  .download-count {
    font-size: 10px;
    padding: 0.15rem 0.3rem;
  }

  .download-icon {
    font-size: 9px;
  }

  .file-name {
    max-width: 120px;
  }

  .file-name {
    font-size: 13px;
  }

  .file-size {
    font-size: 11px;
  }

  .btn {
    padding: 0.2rem 0.5rem;
    font-size: 11px;
  }
}

/* Driver.js 自定义样式 */
:deep(.driverjs-theme) {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
}

:deep(.driverjs-theme .driver-popover-title) {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

:deep(.driverjs-theme .driver-popover-description) {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1rem;
}

:deep(.driverjs-theme .driver-popover-footer) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

:deep(.driverjs-theme .driver-popover-progress-text) {
  font-size: 0.85rem;
  color: #999;
}

:deep(.driverjs-theme .driver-popover-btn) {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-next-btn) {
  background: #3498db;
  color: white;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-next-btn:hover) {
  background: #2980b9;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-prev-btn) {
  background: #ecf0f1;
  color: #333;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-prev-btn:hover) {
  background: #d5dbdb;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-close-btn) {
  background: none;
  color: #999;
  padding: 0.25rem;
  font-size: 1.2rem;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-close-btn:hover) {
  background: #f5f5f5;
  color: #333;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-done-btn) {
  background: #3498db;
  color: white;
}

:deep(.driverjs-theme .driver-popover-btn.driver-popover-done-btn:hover) {
  background: #2980b9;
}

/* 移动端适配 */
@media (max-width: 768px) {
  :deep(.driverjs-theme) {
    max-width: calc(100vw - 24px);
    border-radius: 8px;
  }

  :deep(.driverjs-theme .driver-popover-title) {
    font-size: 1rem;
  }

  :deep(.driverjs-theme .driver-popover-description) {
    font-size: 0.875rem;
  }

  :deep(.driverjs-theme .driver-popover-btn) {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }
}
</style>
